import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {
  firstValueFrom,
  throwError,
  timer,
  TimeoutError,
} from "rxjs";
import { retry, timeout } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { AuthenticationService } from "app/_services/authentication.service";

@Injectable({
  providedIn: "root",
})
export class AgentService {
  private readonly agentApiBaseUrl = `${environment.apiBaseUrl}/bedrock/agent`;
  private readonly invokeTimeoutMs = 60000;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) {}

  /**
   * Generate a unique session ID for the conversation
   * Session ID must be 33+ characters
   */
  generateSessionId(): string {
    return `test-session-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 22)}`;
  }

  async generateSessionIdFromApi(): Promise<string> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ sessionId?: string }>(
          `${this.agentApiBaseUrl}/session`
        )
      );
      if (response?.sessionId) {
        return response.sessionId;
      }
    } catch (error) {
      console.warn("Failed to fetch sessionId from backend, falling back to local generation", error);
    }
    return this.generateSessionId();
  }

  /**
   * Stream the Bedrock agent response via SSE.
   * Calls onToken for each text chunk; resolves with the final answer string.
   */
  async invokeAgentStream(
    userQuestion: string,
    sessionId: string,
    bureauId: string | undefined,
    promptOverrides: Record<string, any> | undefined,
    onToken: (text: string) => void
  ): Promise<string> {
    const requestBody: Record<string, unknown> = {
      userQuestion,
      sessionId,
    };
    if (bureauId) {
      requestBody.bureauId = bureauId;
    }
    if (promptOverrides && Object.keys(promptOverrides).length > 0) {
      requestBody.promptOverrides = promptOverrides;
    }

    const token = this.authService.token() || "";
    const authHeader = token
      ? (token.match(/^Bearer\s+/i) ? token : `Bearer ${token}`)
      : "";
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.invokeTimeoutMs);

    try {
      const response = await fetch(`${this.agentApiBaseUrl}/invoke-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (!response.ok) {
        let message = "Failed to invoke Bedrock agent";
        try {
          const errBody = await response.json();
          message =
            errBody?.error?.message || errBody?.message || message;
        } catch (e) {
          message = `${message} (${response.status})`;
        }
        throw new Error(message);
      }

      if (!response.body) {
        throw new Error("Streaming response body is empty");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalAnswer = "";
      let eventName = "message";

      const flushEvent = (rawEvent: string) => {
        const lines = rawEvent.split(/\r?\n/);
        let dataLines: string[] = [];
        let name = eventName;
        for (const line of lines) {
          if (line.startsWith("event:")) {
            name = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trimStart());
          }
        }
        if (!dataLines.length) return;
        const dataStr = dataLines.join("\n");
        let data: any;
        try {
          data = JSON.parse(dataStr);
        } catch (e) {
          data = { text: dataStr };
        }

        if (name === "token" && data?.text) {
          onToken(String(data.text));
          finalAnswer += String(data.text);
        } else if (name === "final") {
          if (data?.answer != null) {
            finalAnswer = String(data.answer);
          }
        } else if (name === "error") {
          throw new Error(data?.error || "Agent stream error");
        }
        eventName = "message";
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sep;
        while ((sep = buffer.indexOf("\n\n")) >= 0) {
          const rawEvent = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          flushEvent(rawEvent);
        }
      }
      if (buffer.trim()) {
        flushEvent(buffer);
      }

      return finalAnswer;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error(
          "Request timed out while waiting for the AI response. Please try again."
        );
      }
      if (error instanceof TypeError) {
        throw new Error(
          "Unable to reach API endpoint. Please check your network, VPN/proxy, or browser blocking and try again."
        );
      }
      throw error instanceof Error ? error : new Error(String(error));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Invoke the Bedrock agent (non-streaming fallback).
   * Authentication uses the session Authorization header only.
   */
  async invokeAgent(
    userQuestion: string,
    sessionId: string,
    bureauId?: string,
    promptOverrides?: Record<string, any>
  ): Promise<string> {
    try {
      const requestBody: Record<string, unknown> = {
        userQuestion,
        sessionId,
      };
      if (bureauId) {
        requestBody.bureauId = bureauId;
      }
      if (promptOverrides && Object.keys(promptOverrides).length > 0) {
        requestBody.promptOverrides = promptOverrides;
      }

      const apiResponse = await firstValueFrom(
        this.http.post<{
          answer?: string;
          rawResponse?: string;
          data?: unknown;
        }>(
          `${this.agentApiBaseUrl}/invoke`,
          requestBody
        ).pipe(
          timeout(this.invokeTimeoutMs),
          retry({
            count: 2,
            delay: (err: unknown, retryCount: number) => {
              if (err instanceof HttpErrorResponse && err.status === 0) {
                return timer(1000 * retryCount);
              }
              return throwError(() => err);
            },
          })
        )
      );

      if (apiResponse?.answer) {
        return apiResponse.answer;
      }

      if (apiResponse?.rawResponse) {
        return apiResponse.rawResponse;
      }

      return apiResponse ? JSON.stringify(apiResponse) : "";
    } catch (error) {
      console.error("Error invoking agent:", error);

      let message = "Failed to invoke Bedrock agent";
      if (error instanceof HttpErrorResponse) {
        if (error.status === 0) {
          message =
            "Unable to reach API endpoint. Please check your network, VPN/proxy, or browser blocking and try again.";
        } else {
          message =
            error.error?.error?.message ||
            error.error?.message ||
            error.message ||
            message;
        }
      } else if (error instanceof TimeoutError) {
        message =
          "Request timed out while waiting for the AI response. Please try again.";
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      throw new Error(message);
    }
  }
}
